import { AnalyticsService } from '@core/services';
import { OpenSourceContent } from '@core/services/site-content/site-content.types';
import { Inject } from '@rxdi/core';
import { Component, css, html, LitElement, property, unsafeCSS } from '@rxdi/lit-html';
import { rich } from '@shared/rich-text';
import { ArrowIcon, StudioStyles } from '@shared/styles/studio.styles';
import * as ScrollbarCSS from 'bundle-text:../../shared/styles/scrollbar.css';

const fmt = (n: number) => `${(n / 1000).toFixed(1)}K`;

/** Home: our open-source frameworks. */
@Component({
  selector: 'rs-open-source',
  styles: [
    StudioStyles,
    unsafeCSS(ScrollbarCSS),
    css`
      .layout {
        display: grid;
        gap: clamp(24px, 4vw, 48px);
      }

      @media (min-width: 1080px) {
        .layout {
          grid-template-columns: 1fr 1fr;
          align-items: stretch;
        }
      }

      .packages {
        padding: clamp(20px, 3vw, 32px);
      }

      .pk-head {
        display: flex;
        justify-content: space-between;
        font-family: var(--rs-font-mono);
        font-size: 12px;
        color: var(--rs-text-muted);
        margin-bottom: 16px;
      }

      .pk {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 6px 16px;
        padding: 14px 0;
        border-top: 1px solid var(--rs-line);
      }

      .pk a {
        font-family: var(--rs-font-mono);
        font-size: 15px;
        color: var(--rs-text);
        transition: color 0.2s var(--rs-ease);
      }

      .pk a:hover {
        color: var(--rs-signal);
      }

      .pk .num {
        font-family: var(--rs-font-mono);
        font-size: 14px;
        color: var(--rs-signal);
        text-align: right;
      }

      .pk .what {
        grid-column: 1 / -1;
        font-size: 13px;
        color: var(--rs-text-muted);
      }

      .bar {
        grid-column: 1 / -1;
        height: 4px;
        border-radius: 4px;
        background: var(--rs-line);
        overflow: hidden;
      }

      .bar i {
        display: block;
        height: 100%;
        width: var(--w);
        border-radius: 4px;
        background: linear-gradient(90deg, var(--rs-signal), var(--rs-ion));
      }

      .code {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: var(--rs-code-bg);
      }

      .code-head {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 14px 18px;
        border-bottom: 1px solid var(--rs-line);
        font-family: var(--rs-font-mono);
        font-size: 12px;
        color: var(--rs-text-muted);
      }

      .code-head i {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--rs-line-strong);
      }

      .code-head span {
        margin-left: 10px;
      }

      pre {
        flex: 1;
        margin: 0;
        padding: 22px 24px;
        overflow: auto;
        font-family: var(--rs-font-mono);
        font-size: 13.5px;
        line-height: 1.75;
        color: var(--rs-text-2);
      }

      .k {
        color: var(--rs-code-key);
      }
      .f {
        color: var(--rs-code-fn);
      }
      .s {
        color: var(--rs-code-str);
      }
      .d {
        color: var(--rs-code-dec);
      }
      .c {
        color: var(--rs-code-comment);
      }

      .actions {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        margin-top: 32px;
      }
    `,
  ],
  template(this: OpenSourceComponent) {
    const c = this.content;
    if (!c) {
      return html``;
    }
    const max = Math.max(1, ...c.packages.map((p) => Number(p.downloads) || 0));
    return html`
      <section class="section" id="open-source">
        <div class="wrap">
          <div class="section-head split">
            <div>
              <span class="kicker"><span class="index">05</span> Open source</span>
              <h2 class="h2">${rich(c.heading)}</h2>
            </div>
            <p class="lead">${rich(c.lead)}</p>
          </div>

          <div class="layout">
            <div class="card packages">
              <div class="pk-head"><span>package</span><span>${c.packagesNote}</span></div>
              ${c.packages.map(
                (p) => html`
                  <div class="pk">
                    <a href="https://www.npmjs.com/package/${p.name}" target="_blank" rel="noopener">${p.name}</a>
                    <span class="num">${fmt(Number(p.downloads) || 0)}</span>
                    <span class="what">${p.what}</span>
                    <span class="bar"
                      ><i style="--w: ${Math.round(((Number(p.downloads) || 0) / max) * 100)}%"></i
                    ></span>
                  </div>
                `
              )}
            </div>

            <div class="card code">
              <div class="code-head"><i></i><i></i><i></i><span>orders.controller.ts</span></div>
              <pre class="neat-scroll"><code><span class="k">import</span> {
  Controller, GraphQLList, Mutation, Query, Type,
} <span class="k">from</span> <span class="s">'@gapi/core'</span>;

<span class="d">@Controller</span>()
<span class="k">export class</span> <span class="f">OrdersController</span> {
  <span class="k">constructor</span>(<span class="k">private</span> orders: OrdersService) {}

  <span class="c">// a typed GraphQL query in four lines</span>
  <span class="d">@Type</span>(<span class="k">new</span> GraphQLList(OrderType))
  <span class="d">@Query</span>()
  <span class="f">listOrders</span>(root, args, ctx) {
    <span class="k">return</span> <span class="k">this</span>.orders.<span class="f">list</span>(ctx.user.id);
  }

  <span class="d">@Type</span>(OrderType)
  <span class="d">@Mutation</span>({ payload: { type: OrderInput } })
  <span class="f">createOrder</span>(root, { payload }, ctx) {
    <span class="k">return</span> <span class="k">this</span>.orders.<span class="f">create</span>(ctx.user.id, payload);
  }
}

<span class="c">// $ npx @gapi/gcli lambda:create  &rarr;  live on Lambforge</span></code></pre>
            </div>
          </div>

          <div class="actions">
            <a
              class="btn btn-ghost"
              href=${c.githubUrl}
              target="_blank"
              rel="noopener"
              @click=${() => this.analytics.track('oss_open', { target: 'github' })}
            >
              GitHub ${ArrowIcon}
            </a>
            <a
              class="btn btn-ghost"
              href=${c.npmUrl}
              target="_blank"
              rel="noopener"
              @click=${() => this.analytics.track('oss_open', { target: 'npm' })}
            >
              npm ${ArrowIcon}
            </a>
          </div>
        </div>
      </section>
    `;
  },
})
export class OpenSourceComponent extends LitElement {
  @property({ attribute: false }) content: OpenSourceContent;

  @Inject(AnalyticsService)
  private analytics: AnalyticsService;
}
